import { useState } from "react";
import { ChartCard, type ChartCardState } from "../../ChartCard";
import { SubscriptionDetail, type SubscriptionDetailNotice } from "../../billing/SubscriptionDetail";
import styles from "../instanceDetail.module.css";
import { useTenantBilling } from "../../../../hooks/useTenantBilling";
import { useAdminTiers } from "../../../../hooks/useAdminTiers";
import {
  activeItems,
  formatCentsUsd,
  mrrBreakdown,
  mrrCents,
  recurringTotals,
  type MutationResult,
  type Subscription,
} from "../../../../types/billing";

type BillingPanelProps = {
  readonly tenantId: string;
};

const cardState = (status: "loading" | "ready" | "error", subscription: Subscription | null): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (subscription === null) return "empty";
  return "ready";
};

// MRR-style money summary: blended monthly-equivalent, plus the real cadence
// in parens when the subscription has annual lines.
const recurringSummary = (subscription: Subscription): string => {
  const totals = recurringTotals(activeItems(subscription));
  const breakdown = mrrBreakdown(totals);
  const mrr = `${formatCentsUsd(mrrCents(totals))}/mo`;
  return breakdown === null ? mrr : `${mrr} (${breakdown})`;
};

// Per-tenant billing card on the instance detail dashboard. Self-fetches the
// tenant's subscription + entitlement and exposes the slot mutations, the
// portal, and a manual sync. Mirrors the other self-fetching panels (Snapshots,
// Audit) that take just a tenantId.
export const BillingPanel = ({ tenantId }: BillingPanelProps): JSX.Element => {
  const billing = useTenantBilling(tenantId);
  const { subscription, entitlement } = billing;
  const { tiers } = useAdminTiers();
  const activeTiers = tiers.filter((t) => t.active).map((t) => ({ tierCode: t.tierCode, displayName: t.displayName }));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SubscriptionDetailNotice>(null);

  const run = async (action: () => Promise<MutationResult>, okText: string): Promise<void> => {
    setBusy(true);
    setNotice(null);
    const result = await action();
    setNotice(result.ok ? { tone: "ok", text: okText } : { tone: "error", text: result.message });
    setBusy(false);
  };

  const handlePortal = async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    const result = await billing.openPortal();
    if (result.ok) window.open(result.url, "_blank", "noopener,noreferrer");
    else setNotice({ tone: "error", text: result.message });
    setBusy(false);
  };

  const subtitle = subscription != null ? `${subscription.status} · ${recurringSummary(subscription)}` : undefined;

  return (
    <ChartCard
      title="Billing"
      subtitle={subtitle}
      wide
      state={cardState(billing.status, subscription)}
      emptyText="No subscription linked to this tenant yet."
      errorText={billing.error ?? undefined}
    >
      <div className={styles.hScroll}>
        {subscription != null && (
          <SubscriptionDetail
            subscription={subscription}
            entitlement={entitlement}
            tiers={activeTiers}
            busy={busy}
            notice={notice}
            onChangeQuantity={(si, qty) => void run(() => billing.changeItemQuantity(si, qty), "Quantity updated.")}
            onRemoveItem={(si) => void run(() => billing.removeItem(si), "Item removed.")}
            onSetTier={(tier, interval) => void run(() => billing.setTier(tier, interval), "Plan updated.")}
            onSync={() => void run(() => billing.sync(), "Synced from Stripe.")}
            onOpenPortal={() => void handlePortal()}
          />
        )}
      </div>
    </ChartCard>
  );
};
